import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "../..";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearSelectedCompanyWithCollaborativesAction,
  companyState,
} from "../../app/reducers/company-slice";
import {
  CompanyWithCollaborativesQueryKey,
  useCompanyWithCollaborativesPageAllQuery,
  useCreateCompanyWithCollaborativesMutation,
  useUpdateCompanyWithCollaborativesMutation,
} from "../../query/company-query";
import theme from "../../style/theme";
import { CompanyWithCollaborativesDto } from "../../types/company/company-dto";
import { Confirm } from "../../types/confirm";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { validateCompanyPayload } from "../../utils/validator/company";
import { validatePhoneNumber } from "../../utils/validator/phone-number";
import { Selector } from "../selector/Selector";
import { GeneralTextField } from "../text-field/GeneralTextField";
import { DialogFooter } from "./DialogFooter";
import { DialogTitle } from "./DialogTitle";
import {PermissionLabels} from "../../utils/permission/permission";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface Props {
  handleClickDialogClose: () => void;
}

const initCompanyWithCollaborativesForm = {
  name: "",
  phone: "",
};

const initCompanyWithCollaborativesPayloadValidator = {
  name: false,
  phone: false,
};

export const CompanyWithCollaborativesFormDialog: React.FC<Props> = ({
  handleClickDialogClose,
}) => {
  const dispatch = useAppDispatch();
  const {
    selectedCompanyWithCollaboratives,
    companyWithCollaborativesPageAll,
  } = useAppSelector(companyState);
  const [companyWithCollaborativesForm, setCompanyWithCollaborativesForm] =
    useState(initCompanyWithCollaborativesForm);
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  // const [permissionMap, setPermissionMap] = useState<{
  //   [key: string]: string;
  // }>({});
  const [localAgencyMap, setLocalAgencyMap] = useState<{
    [key: string]: string;
  }>({});

  const [
    companyWithCollaborativesPayloadValidator,
    setCompanyWithCollaborativesPayloadValidator,
  ] = useState(initCompanyWithCollaborativesPayloadValidator);
  const createCompanyWithCollaborativesMutation =
    useCreateCompanyWithCollaborativesMutation();
  const updateCompanyWithCollaborativesMutation =
    useUpdateCompanyWithCollaborativesMutation();

  useCompanyWithCollaborativesPageAllQuery();

  const handleChangeCompanyWithCollaborativesFormTextField = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCompanyWithCollaborativesForm({
      ...companyWithCollaborativesForm,
      [e.target.name]: e.target.value,
    });
  };

  const setCompanies = async () => {
    try {
      const localAgencyMapTemp: { [key: string]: string } = {};
      companyWithCollaborativesPageAll.contents.forEach(
        (companyWithCollaborativesDto: CompanyWithCollaborativesDto) => {
          if (
            selectedCompanyWithCollaboratives?.id !==
            companyWithCollaborativesDto.id
          ) {
            localAgencyMapTemp[companyWithCollaborativesDto.id] =
              companyWithCollaborativesDto.name;
          }
        }
      );
      setLocalAgencyMap(localAgencyMapTemp);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickCompanyWithCollaborativesItem = (key: string) => {
    setSelectedAgencyIds((prevStatus) => {
      if (prevStatus.includes(key)) {
        return prevStatus.filter((v) => v !== key);
      } else {
        return [...prevStatus, key];
      }
    });
  };

  const handleClickCompanyTMSPermission = (key: string) => {
    setSelectedPermissions((prevStatus) => {
      if (prevStatus.includes(key)) {
        return prevStatus.filter((v) => v !== key);
      } else {
        return [...prevStatus, key];
      }
    });
  };

  const handleClickCloseButton = () => {
    if (selectedCompanyWithCollaboratives) {
      dispatch(clearSelectedCompanyWithCollaborativesAction());
    } else {
      handleClickDialogClose();
    }
  };

  const handleClickCreateCompanyWithCollaborativesUserButton = async () => {
    try {
      const payload = {
        name: companyWithCollaborativesForm.name,
        phone: companyWithCollaborativesForm.phone,
        agencyIds: selectedAgencyIds,
        roles: selectedPermissions,
      };

      if (validateCompanyPayload(payload).hasError) {
        setCompanyWithCollaborativesPayloadValidator(
          validateCompanyPayload(payload).validationResults
        );
        toast.error(Confirm.Error);
        return;
      }

      createCompanyWithCollaborativesMutation.mutate(payload, {
        onSuccess,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickUpdateCompanyWithCollaborativesUserButton = async () => {
    try {
      if (!selectedCompanyWithCollaboratives) {
        return;
      }
      const payload = {
        name: companyWithCollaborativesForm.name,
        phone: companyWithCollaborativesForm.phone,
        agencyIds: selectedAgencyIds,
        roles: selectedPermissions,
      };

      if (validatePhoneNumber(payload.phone)) {
        setCompanyWithCollaborativesPayloadValidator({
          ...companyWithCollaborativesPayloadValidator,
          phone: true,
        });
        toast.error(Confirm.Error);
        return;
      }

      updateCompanyWithCollaborativesMutation.mutate(
        {
          id: selectedCompanyWithCollaboratives.id,
          payload,
        },
        {
          onSuccess,
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const initForm = () => {
    setCompanyWithCollaborativesForm(initCompanyWithCollaborativesForm);
    setSelectedAgencyIds([]);
    setSelectedPermissions([]);
  };

  const onSuccess = () => {
    initForm();
    handleClickCloseButton();
  };

  const initSelectedCompanyWithCollaboratives = () => {
    if (selectedCompanyWithCollaboratives) {
      setCompanyWithCollaborativesForm({
        name: selectedCompanyWithCollaboratives.name,
        phone: selectedCompanyWithCollaboratives.phone,
      });
      setSelectedAgencyIds(
        selectedCompanyWithCollaboratives.agencies.map((agency) => agency.id)
      );
      setSelectedPermissions(
          selectedCompanyWithCollaboratives.roles
      );
    } else {
      initForm();
    }
  };

  const handleClickInitButton = () => {
    initSelectedCompanyWithCollaboratives();
  };

  useEffect(() => {
    queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.PageAll);
  }, []);

  useDidMountEffect(() => {
    setCompanies();
  }, [companyWithCollaborativesPageAll]);

  useDidMountEffect(() => {
    initSelectedCompanyWithCollaboratives();
  }, [localAgencyMap]);

  return (
    <BootstrapDialog
      onClose={handleClickCloseButton}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <CompanyWithCollaborativesFormDialogContainer>
        <DialogTitle
          title={`회사 ${
            selectedCompanyWithCollaboratives ? "수정완료" : "등록하기"
          }`}
          onClick={handleClickCloseButton}
        />
        <DialogBodyContainer>
          <SpaceBetweenRaw>
            <FieldContainer>회사명</FieldContainer>
            <GeneralTextField
                placeholder={"회사명을 입력해주세요"}
                name={"name"}
                value={companyWithCollaborativesForm.name}
                onChange={handleChangeCompanyWithCollaborativesFormTextField}
                width="23.25rem"
                isError={companyWithCollaborativesPayloadValidator.name}
            />
          </SpaceBetweenRaw>
          {(selectedCompanyWithCollaboratives && selectedCompanyWithCollaboratives.clients.length > 0) && (
              <SpaceBetweenRaw>
                <FieldContainer>담당 화주사</FieldContainer>
                <FieldTextLabel>
                  {selectedCompanyWithCollaboratives.clients
                      .map((c) => c.name)
                      .join(", ")}
                </FieldTextLabel>
              </SpaceBetweenRaw>
          )}
          <SpaceBetweenRaw>
            <FieldContainer>담당 대행사</FieldContainer>
            <Selector
              keys={selectedAgencyIds}
              defaultKey={""}
              candidateMap={localAgencyMap}
              onClickFilterMultiSelectItem={
                handleClickCompanyWithCollaborativesItem
              }
              width="23.25rem"
              placeholder="담당 대행사"
            />
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>TMS 권한</FieldContainer>
            <Selector
                keys={selectedPermissions}
                defaultKey={""}
                candidateMap={PermissionLabels}
                onClickFilterMultiSelectItem={
                  handleClickCompanyTMSPermission
                }
                width="23.25rem"
                placeholder="TMS 권한"
            />
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>전화번호</FieldContainer>
            <GeneralTextField
              placeholder={"01000000000"}
              name={"phone"}
              value={companyWithCollaborativesForm.phone}
              onChange={handleChangeCompanyWithCollaborativesFormTextField}
              width="23.25rem"
              isError={companyWithCollaborativesPayloadValidator.phone}
            />
          </SpaceBetweenRaw>
        </DialogBodyContainer>
        <DialogFooter
          confirmButtonText={
            selectedCompanyWithCollaboratives ? "수정완료" : "등록하기"
          }
          disabled={
            createCompanyWithCollaborativesMutation.isLoading ||
            updateCompanyWithCollaborativesMutation.isLoading
          }
          onClickInitButton={handleClickInitButton}
          onClickConfirmButton={
            selectedCompanyWithCollaboratives
              ? handleClickUpdateCompanyWithCollaborativesUserButton
              : handleClickCreateCompanyWithCollaborativesUserButton
          }
        />
      </CompanyWithCollaborativesFormDialogContainer>
    </BootstrapDialog>
  );
};

const CompanyWithCollaborativesFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpaceBetweenRaw = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const FieldContainer = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const FieldTextLabel = styled.div`
  width: 23.25rem;
  font-size: 14px;
  font-weight: 400;
  color: ${theme.palette.primitives.gray900};
  padding: 6px 0;
`;

const DialogBodyContainer = styled.div`
  overflow-y: scroll;
  padding: 20px 40px 24px;
  width: 500px;
`;
